import React, { useState, useCallback } from "react";
import { Accordion, AccordionItem, Button, Rows, Text, Title } from "@canva/app-ui-kit";
import { addPage, addElementAtPoint, getCurrentPageContext } from "@canva/design";
import { upload } from "@canva/asset";
import { api, type MetricsResponse, type VarianceResponse, type VarianceRow } from "../api/client";
import type {
  TextElementAtPoint,
  ImageElementAtPoint,
  ElementAtPoint,
  TableElement,
  Cell,
} from "@canva/design";
import { VarianceConfigCard, type VarianceConfig } from "./variance-config";
import { BoardDeckConfigCard, type BoardDeckConfig } from "./board-deck-config";
import { InvestorConfigCard, type InvestorConfig } from "./investor-config";
import type { MetricKey } from "./slide-options";

// Layout constants are authored at 1920x1080 (Canva Presentation default) and
// scaled proportionally to whatever the user's current page actually is —
// falls back to 1920x1080 if the design is unbounded (Whiteboard/Doc).
const REF_W = 1920;
const REF_H = 1080;
const DEFAULT_DIMS = { width: REF_W, height: REF_H };

async function getPageDims(): Promise<{ width: number; height: number }> {
  try {
    const ctx = await getCurrentPageContext();
    return ctx.dimensions ?? DEFAULT_DIMS;
  } catch {
    return DEFAULT_DIMS;
  }
}

interface SlideProps {
  scenarioId: string;
  month: string;
}

type SlideStatus = "idle" | "loading" | "done" | "error";

export function SlidesSection({ scenarioId, month }: SlideProps) {
  const [inserting, setInserting] = useState<string | null>(null);
  const [boardDeckError, setBoardDeckError] = useState<string | null>(null);
  const [investorError, setInvestorError] = useState<string | null>(null);
  const [varianceError, setVarianceError] = useState<string | null>(null);
  const [varianceWarning, setVarianceWarning] = useState<string | null>(null);

  const insertBoardDeckSlide = useCallback(
    async (config: BoardDeckConfig) => {
      setInserting("board-deck");
      setBoardDeckError(null);
      try {
        const [dims, metrics, chart] = await Promise.all([
          getPageDims(),
          api.getMetrics(scenarioId, month),
          api.getChart(scenarioId, config.chartType, config.historyMonths, month),
        ]);
        const elements = buildBoardDeckSlide(metrics, chart, config.metrics, dims);
        const resolvedElements = await resolveImageElements(elements);
        await addPage({
          background: { color: "#0f172a" },
          dimensions: dims,
          elements: resolvedElements,
        });
      } catch (err) {
        setBoardDeckError(err instanceof Error ? err.message : "Failed to create slide");
      } finally {
        setInserting(null);
      }
    },
    [scenarioId, month]
  );

  const insertInvestorSlide = useCallback(
    async (config: InvestorConfig) => {
      setInserting("investor");
      setInvestorError(null);
      try {
        const [dims, metrics, primaryChart, secondaryChart] = await Promise.all([
          getPageDims(),
          api.getMetrics(scenarioId, month),
          api.getChart(scenarioId, config.primaryChart, config.historyMonths, month),
          api.getChart(scenarioId, config.secondaryChart, config.historyMonths, month),
        ]);
        const elements = buildInvestorSlide(metrics, primaryChart, secondaryChart, config.metrics, dims);
        const resolvedElements = await resolveImageElements(elements);
        await addPage({
          background: { color: "#0f172a" },
          dimensions: dims,
          elements: resolvedElements,
        });
      } catch (err) {
        setInvestorError(err instanceof Error ? err.message : "Failed to create slide");
      } finally {
        setInserting(null);
      }
    },
    [scenarioId, month]
  );

  const insertVarianceSlide = useCallback(
    async (config: VarianceConfig) => {
      setInserting("variance");
      setVarianceError(null);
      setVarianceWarning(null);
      try {
        const [metrics, variance] = await Promise.all([
          api.getMetrics(scenarioId, month),
          api.getVariance(
            scenarioId,
            variancePeriodStart(month, config.period),
            month,
            {
              comparisonType: config.comparison,
              thresholdPercent: config.thresholdPercent,
            }
          ),
        ]);

        if (variance.message || variance.rows.length === 0) {
          setVarianceError(variance.message ?? "No variance data for this period");
          return;
        }

        const { table, truncated } = buildVarianceTable(metrics, variance, config);
        const dims = await getPageDims();
        await addPage({ background: { color: "#0f172a" }, dimensions: dims });
        await addElementAtPoint(table);
        if (truncated > 0) {
          setVarianceWarning(
            `Table trimmed to fit Canva's cell limit — ${truncated} line item${truncated === 1 ? "" : "s"} not shown. Try Summary only or raise the threshold.`
          );
        }
      } catch (err) {
        setVarianceError(err instanceof Error ? err.message : "Failed to create slide");
      } finally {
        setInserting(null);
      }
    },
    [scenarioId, month]
  );

  return (
    <Rows spacing="1u">
      <Title size="xsmall">Slides</Title>
      <Accordion>
        <AccordionItem title="Board Deck">
          <BoardDeckConfigCard
            scenarioId={scenarioId}
            onInsert={insertBoardDeckSlide}
            loading={inserting === "board-deck"}
            error={boardDeckError}
          />
        </AccordionItem>
        <AccordionItem title="Investor Slide">
          <InvestorConfigCard
            scenarioId={scenarioId}
            onInsert={insertInvestorSlide}
            loading={inserting === "investor"}
            error={investorError}
          />
        </AccordionItem>
        <AccordionItem title="Variance Analysis">
          <VarianceConfigCard
            scenarioId={scenarioId}
            onInsert={insertVarianceSlide}
            loading={inserting === "variance"}
            error={varianceError}
            warning={varianceWarning}
          />
        </AccordionItem>
      </Accordion>
    </Rows>
  );
}

// ---------------------------------------------------------------------------
// Board Deck Slide
// ---------------------------------------------------------------------------
// Layout:
//   Title bar
//   4 KPI blocks in a row (ARR, Burn Rate, Cash on Hand, Runway)
//   Revenue chart spanning the bottom half

function buildBoardDeckSlide(
  metrics: MetricsResponse,
  revenueChart: { dataUrl: string; width: number; height: number },
  selectedKeys: MetricKey[],
  dims: { width: number; height: number }
): ElementAtPoint[] {
  const { width: W, height: H } = dims;
  const s = W / REF_W;
  const PAD = 80 * s;
  const elements: ElementAtPoint[] = [];

  // Title + subtitle
  elements.push(textEl("Board Deck", PAD, PAD, W - PAD * 2, 56 * s, "#ffffff", "start", "bold"));
  elements.push(textEl(formatPeriod(metrics.period), PAD, PAD + 80 * s, 600 * s, 26 * s, "#94a3b8"));

  // KPI row — up to 4 selected metrics, adaptive width
  const kpiY = 220 * s;
  const kpis = selectedKeys.slice(0, 4).flatMap((key) => {
    const metric = metrics.metrics[key];
    return metric ? [{ label: metric.label, value: metric.formatted, change: metric.change }] : [];
  });
  const gap = 60 * s;
  const kpiCount = Math.max(kpis.length, 1);
  const kpiW = (W - PAD * 2 - gap * (kpiCount - 1)) / kpiCount;
  kpis.forEach((kpi, i) => {
    const x = PAD + i * (kpiW + gap);
    elements.push(...kpiBlock(kpi.label, kpi.value, kpi.change, x, kpiY, kpiW, s));
  });

  // Chart — bottom half
  const chartW = W - PAD * 2;
  const chartH = 520 * s;
  const chartY = H - PAD - chartH;
  elements.push(imageEl(revenueChart.dataUrl, PAD, chartY, chartW, chartH));

  return elements;
}

// ---------------------------------------------------------------------------
// Investor Slide
// ---------------------------------------------------------------------------
// Layout:
//   Title bar
//   3 KPIs in a row (Revenue, Customer Growth, High-Level Spend)
//   P&L chart (left), Expense breakdown (right)

function buildInvestorSlide(
  metrics: MetricsResponse,
  primaryChart: { dataUrl: string; width: number; height: number },
  secondaryChart: { dataUrl: string; width: number; height: number },
  selectedKeys: MetricKey[],
  dims: { width: number; height: number }
): ElementAtPoint[] {
  const { width: W, height: H } = dims;
  const s = W / REF_W;
  const PAD = 80 * s;
  const elements: ElementAtPoint[] = [];

  elements.push(textEl("Investor Overview", PAD, PAD, W - PAD * 2, 56 * s, "#ffffff", "start", "bold"));
  elements.push(textEl(formatPeriod(metrics.period), PAD, PAD + 80 * s, 600 * s, 26 * s, "#94a3b8"));

  // KPI row — up to 3 selected metrics, adaptive width
  const kpiY = 220 * s;
  const kpis = selectedKeys.slice(0, 3).flatMap((key) => {
    const metric = metrics.metrics[key];
    return metric ? [{ label: metric.label, value: metric.formatted, change: metric.change }] : [];
  });
  const gap = 60 * s;
  const kpiCount = Math.max(kpis.length, 1);
  const kpiW = (W - PAD * 2 - gap * (kpiCount - 1)) / kpiCount;
  kpis.forEach((kpi, i) => {
    const x = PAD + i * (kpiW + gap);
    elements.push(...kpiBlock(kpi.label, kpi.value, kpi.change, x, kpiY, kpiW, s));
  });

  // Two charts side by side — bottom half
  const chartY = 480 * s;
  const chartH = H - PAD - chartY;
  const chartGap = 40 * s;
  const chartW = (W - PAD * 2 - chartGap) / 2;
  elements.push(imageEl(primaryChart.dataUrl, PAD, chartY, chartW, chartH));
  elements.push(imageEl(secondaryChart.dataUrl, PAD + chartW + chartGap, chartY, chartW, chartH));

  return elements;
}

// ---------------------------------------------------------------------------
// Variance Table
// ---------------------------------------------------------------------------
// Renders the variance as a single Canva TableElement — one element regardless
// of row count, so we sidestep the 100-element-per-page cap. Title, KPIs,
// column headers, section headers, and data rows all live in the table.

const COLS = 5;
const BG = "#0f172a";
const HEADER_BG = "#1e293b";
const SECTION_BG = "#312e81";
const GREEN = "#22c55e";
const RED = "#ef4444";
const TEXT = "#e2e8f0";
const DIM = "#94a3b8";

function cell(
  value: string,
  attrs: {
    fontSize?: number;
    fontWeight?: "normal" | "bold";
    color?: string;
    backgroundColor?: string;
    textAlign?: "start" | "center" | "end";
    colSpan?: number;
  } = {}
): Cell {
  const { colSpan, ...textAttrs } = attrs;
  return {
    type: "string",
    value,
    colSpan,
    attributes: textAttrs,
  };
}

function emptyCell(backgroundColor?: string): Cell {
  return {
    type: "empty",
    attributes: backgroundColor ? { backgroundColor } : undefined,
  };
}

/** Pad a row whose first cell has `colSpan=COLS` with nulls to match column count. */
function spannedRow(c: Cell): { cells: (Cell | null | undefined)[] } {
  return { cells: [c, null, null, null, null] };
}

function buildVarianceTable(
  metrics: MetricsResponse,
  variance: VarianceResponse,
  config: VarianceConfig
): { table: TableElement; truncated: number } {
  const filteredRows = config.showOnlyOverThreshold
    ? variance.rows.filter((r) => r.isOverThreshold)
    : variance.rows.slice(0, config.maxRows);

  const labels = varianceLabels(config.comparison, metrics.period, config.period);

  // Group rows by tableName (preserves backend's statement ordering)
  const groups: Array<{ name: string | null; rows: VarianceRow[] }> = [];
  for (const row of filteredRows) {
    const key = row.tableName ?? null;
    const last = groups[groups.length - 1];
    if (last && last.name === key) {
      last.rows.push(row);
    } else {
      groups.push({ name: key, rows: [row] });
    }
  }

  // Canva tables max out at 225 cells = 45 rows × 5 cols. Reserve a row of
  // safety margin plus 5 rows for the title/subtitle/KPI/column-header band.
  const MAX_ROWS = 44;
  const HEADER_ROWS = 5;

  // Reserve rows for the trailing Profitability section so it doesn't get
  // squeezed out when there are many data rows — it's more signal than any
  // individual line item.
  const computedLines = variance.computedLines ?? [];
  const profitabilityReserve =
    computedLines.length > 0 ? 1 + computedLines.length : 0; // 1 section header + lines

  let remainingRows = MAX_ROWS - HEADER_ROWS - profitabilityReserve;
  let truncated = 0;

  const rows: { cells: (Cell | null | undefined)[] }[] = [];

  // Title (merged across all cols)
  rows.push(spannedRow(cell("Variance Analysis", {
    fontSize: 32, fontWeight: "bold", color: "#ffffff", backgroundColor: BG, colSpan: COLS,
  })));

  // Subtitle (merged)
  rows.push(spannedRow(cell(labels.subtitle, {
    fontSize: 18, color: DIM, backgroundColor: BG, colSpan: COLS,
  })));

  // KPI labels
  rows.push({
    cells: [
      cell(`Total ${labels.base}`, { fontSize: 14, color: DIM, backgroundColor: BG }),
      cell(`Total ${labels.compare}`, { fontSize: 14, color: DIM, backgroundColor: BG }),
      cell("Total Variance", { fontSize: 14, color: DIM, backgroundColor: BG }),
      emptyCell(BG),
      emptyCell(BG),
    ],
  });

  // KPI values
  rows.push({
    cells: [
      cell(variance.summary.totalForecast, { fontSize: 26, fontWeight: "bold", color: "#ffffff", backgroundColor: BG }),
      cell(variance.summary.totalActual, { fontSize: 26, fontWeight: "bold", color: "#ffffff", backgroundColor: BG }),
      cell(variance.summary.totalVariance, { fontSize: 26, fontWeight: "bold", color: "#ffffff", backgroundColor: BG }),
      emptyCell(BG),
      emptyCell(BG),
    ],
  });

  // Column headers
  rows.push({
    cells: [
      cell("Line Item", { fontSize: 14, fontWeight: "bold", color: DIM, backgroundColor: HEADER_BG }),
      cell(labels.base, { fontSize: 14, fontWeight: "bold", color: DIM, backgroundColor: HEADER_BG, textAlign: "end" }),
      cell(labels.compare, { fontSize: 14, fontWeight: "bold", color: DIM, backgroundColor: HEADER_BG, textAlign: "end" }),
      cell("Variance", { fontSize: 14, fontWeight: "bold", color: DIM, backgroundColor: HEADER_BG, textAlign: "end" }),
      cell("% Var", { fontSize: 14, fontWeight: "bold", color: DIM, backgroundColor: HEADER_BG, textAlign: "end" }),
    ],
  });

  // Index backend-provided section totals by tableName for quick lookup.
  const sectionTotalByName = new Map(
    (variance.sectionTotals ?? []).map((t) => [t.tableName, t])
  );

  // Section headers + data rows + section totals, with a cell-count budget.
  for (const group of groups) {
    const hasHeader = !!group.name;
    const total = group.name ? sectionTotalByName.get(group.name) : undefined;
    const hasTotal = !!total;
    const overhead = (hasHeader ? 1 : 0) + (hasTotal ? 1 : 0);

    // Without summaryOnly, a group needs at least one data row to be worth showing.
    // With summaryOnly, overhead alone is enough.
    const minRowsForGroup = overhead + (config.summaryOnly ? 0 : 1);
    if (remainingRows < minRowsForGroup) {
      truncated += group.rows.length;
      continue;
    }

    if (hasHeader) {
      rows.push(spannedRow(cell(group.name!, {
        fontSize: 16, fontWeight: "bold", color: "#a78bfa", backgroundColor: SECTION_BG, colSpan: COLS,
      })));
      remainingRows--;
    }

    if (!config.summaryOnly) {
      // Save one slot for the total if we plan to render it.
      const dataRowBudget = remainingRows - (hasTotal ? 1 : 0);
      const dataRowsShown = Math.min(group.rows.length, Math.max(0, dataRowBudget));
      for (let i = 0; i < dataRowsShown; i++) {
        const row = group.rows[i];
        const varColor = row.isFavorable ? GREEN : RED;
        const weight = row.isOverThreshold ? "bold" : "normal";
        rows.push({
          cells: [
            cell(row.lineName, { fontSize: 15, color: TEXT, backgroundColor: BG }),
            cell(row.forecast, { fontSize: 15, color: TEXT, backgroundColor: BG, textAlign: "end" }),
            cell(row.actual, { fontSize: 15, color: TEXT, backgroundColor: BG, textAlign: "end" }),
            cell(row.variance, { fontSize: 15, fontWeight: weight, color: varColor, backgroundColor: BG, textAlign: "end" }),
            cell(
              `${row.variancePercent > 0 ? "+" : ""}${row.variancePercent.toFixed(1)}%`,
              { fontSize: 15, fontWeight: weight, color: varColor, backgroundColor: BG, textAlign: "end" }
            ),
          ],
        });
      }
      truncated += group.rows.length - dataRowsShown;
      remainingRows -= dataRowsShown;
    }

    if (total) {
      const varColor = total.isFavorable ? GREEN : RED;
      rows.push({
        cells: [
          cell(`Total ${total.tableName}`, { fontSize: 15, fontWeight: "bold", color: TEXT, backgroundColor: HEADER_BG }),
          cell(total.forecast, { fontSize: 15, fontWeight: "bold", color: TEXT, backgroundColor: HEADER_BG, textAlign: "end" }),
          cell(total.actual, { fontSize: 15, fontWeight: "bold", color: TEXT, backgroundColor: HEADER_BG, textAlign: "end" }),
          cell(total.variance, { fontSize: 15, fontWeight: "bold", color: varColor, backgroundColor: HEADER_BG, textAlign: "end" }),
          cell(
            `${total.variancePercent > 0 ? "+" : ""}${total.variancePercent.toFixed(1)}%`,
            { fontSize: 15, fontWeight: "bold", color: varColor, backgroundColor: HEADER_BG, textAlign: "end" }
          ),
        ],
      });
      remainingRows--;
    }
  }

  // Profitability section (Earnings / Profit (Loss) from the Deductions table).
  // Always rendered when present — budget for these was reserved up top.
  if (computedLines.length > 0) {
    rows.push(spannedRow(cell("Profitability", {
      fontSize: 16, fontWeight: "bold", color: "#fbbf24", backgroundColor: SECTION_BG, colSpan: COLS,
    })));
    for (const line of computedLines) {
      const varColor = line.isFavorable ? GREEN : RED;
      rows.push({
        cells: [
          cell(line.name, { fontSize: 15, fontWeight: "bold", color: TEXT, backgroundColor: HEADER_BG }),
          cell(line.forecast, { fontSize: 15, fontWeight: "bold", color: TEXT, backgroundColor: HEADER_BG, textAlign: "end" }),
          cell(line.actual, { fontSize: 15, fontWeight: "bold", color: TEXT, backgroundColor: HEADER_BG, textAlign: "end" }),
          cell(line.variance, { fontSize: 15, fontWeight: "bold", color: varColor, backgroundColor: HEADER_BG, textAlign: "end" }),
          cell(
            `${line.variancePercent > 0 ? "+" : ""}${line.variancePercent.toFixed(1)}%`,
            { fontSize: 15, fontWeight: "bold", color: varColor, backgroundColor: HEADER_BG, textAlign: "end" }
          ),
        ],
      });
    }
  }

  return { table: { type: "table", rows }, truncated };
}

// ---------------------------------------------------------------------------
// Element helpers
// ---------------------------------------------------------------------------

/** Plain text element with positioning. */
function textEl(
  text: string,
  left: number,
  top: number,
  width: number,
  fontSize: number,
  color = "#ffffff",
  textAlign: "start" | "center" | "end" = "start",
  fontWeight: "normal" | "bold" = "normal"
): TextElementAtPoint {
  return {
    type: "text",
    children: [text],
    top,
    left,
    width,
    fontSize,
    color,
    textAlign,
    fontWeight,
  } as TextElementAtPoint;
}

/** KPI block: label (small gray) + value (large white) + optional change indicator. */
function kpiBlock(
  label: string,
  value: string,
  change: number | null | undefined,
  left: number,
  top: number,
  width: number,
  s: number = 1
): TextElementAtPoint[] {
  const els: TextElementAtPoint[] = [];

  // Label
  els.push(textEl(label, left, top, width, 22 * s, "#94a3b8"));

  // Value
  els.push(textEl(value, left, top + 32 * s, width, 52 * s, "#ffffff", "start", "bold"));

  // Change
  if (change != null && change !== 0) {
    const arrow = change > 0 ? "▲" : "▼";
    const color = change > 0 ? "#22c55e" : "#ef4444";
    els.push(textEl(`${arrow} ${Math.abs(change)}% MoM`, left, top + 90 * s, width, 20 * s, color));
  }

  return els;
}

/**
 * Image element placeholder — stores dataUrl temporarily.
 * resolveImageElements() uploads to Canva and replaces with real refs.
 */
function imageEl(
  dataUrl: string,
  left: number,
  top: number,
  width: number,
  height: number
): ElementAtPoint {
  // Stash dataUrl in a custom property; resolved before addPage
  return {
    type: "image",
    ref: dataUrl as any, // placeholder — resolved by resolveImageElements
    altText: { text: "Chart", decorative: true },
    top,
    left,
    width,
    height,
  } as ImageElementAtPoint;
}

/**
 * Walk through elements, find image placeholders with data: URLs,
 * upload them to Canva, and replace with real ImageRefs.
 */
async function resolveImageElements(elements: ElementAtPoint[]): Promise<ElementAtPoint[]> {
  const resolved = await Promise.all(
    elements.map(async (el) => {
      if (el.type !== "image") return el;
      const img = el as ImageElementAtPoint;
      const ref = img.ref as unknown as string;
      if (!ref || !ref.startsWith("data:")) return el;

      const asset = await upload({
        type: "image",
        mimeType: "image/png",
        url: ref,
        thumbnailUrl: ref,
        aiDisclosure: "none",
      });

      return {
        ...img,
        ref: asset.ref,
      } as ImageElementAtPoint;
    })
  );
  return resolved;
}

/**
 * Labels for the variance slide based on comparison + period type.
 * `base` = left numeric column / "Total X" (the comparison basis)
 * `compare` = right numeric column / "Total Y" (the period under review)
 */
function varianceLabels(
  comparison: VarianceConfig["comparison"],
  endMonth: string,
  period: VarianceConfig["period"]
): {
  subtitle: string;
  base: string;
  compare: string;
} {
  const periodPrefix = period === "qtd" ? "QTD " : period === "ytd" ? "YTD " : "";
  const endLabel = `${periodPrefix}${formatPeriod(endMonth)}`;

  if (comparison === "actuals_vs_previous_period") {
    const prevLabel =
      period === "month"
        ? formatPeriod(monthsAgo(endMonth, 1))
        : "Prior Period";
    return {
      subtitle: `${endLabel} vs ${prevLabel}`,
      base: "Previous",
      compare: "Current",
    };
  }
  return {
    subtitle: `Forecast vs Actuals — ${endLabel}`,
    base: "Forecast",
    compare: "Actual",
  };
}

function formatPeriod(month: string): string {
  const [y, m] = month.split("-").map(Number);
  const date = new Date(y, m - 1, 1);
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

function monthsAgo(month: string, n: number): string {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(y, m - 1 - n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * Resolve the variance period-start from an end month and period type.
 * - month: single month (start == end)
 * - qtd:   start of the calendar quarter containing `month`
 * - ytd:   January of the same year
 */
function variancePeriodStart(
  month: string,
  period: "month" | "qtd" | "ytd"
): string {
  const [y, m] = month.split("-").map(Number);
  if (period === "month") return month;
  if (period === "ytd") return `${y}-01`;
  const quarterStart = Math.floor((m - 1) / 3) * 3 + 1;
  return `${y}-${String(quarterStart).padStart(2, "0")}`;
}
